import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import MainLoader from "../../../components/loading/MainLoader";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import Title from "../../../components/title/Title";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../../const/URL";
import { FiEye } from "react-icons/fi";
import Button from "@mui/material/Button";
import { getCustomerReferenceNumber } from "../../../services/localStorage";
import EmptyPlaceholder from "../../../components/emptyPlaceholder/EmptyPlaceholder";
import { Box } from "@mui/material";

const useDynamicColumnWidth = (columnCount: number) => {
  const [columnWidth, setColumnWidth] = useState(0);

  useEffect(() => {
    const calculateWidth = () => {
      const availableWidth = window.innerWidth - 410;
      return Math.max(100, availableWidth / columnCount);
    };

    setColumnWidth(calculateWidth());

    const handleResize = () => {
      setColumnWidth(calculateWidth());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [columnCount]);

  return columnWidth;
};

const OngoingOrderComplainList = () => {
  const columnCount = 6;
  const columnWidth = useDynamicColumnWidth(columnCount);

  const columns: GridColDef[] = [
    {
      field: "orderNumber",
      headerName: "Reference No",
      width: Number(columnWidth),
    },
    {
      field: "sltOrderSubType",
      headerName: "Order Type",
      width: Number(columnWidth),
    },
    {
      field: "serviceId",
      headerName: "Service ID",
      width: Number(columnWidth),
    },
    {
      field: "accountAddressCity",
      headerName: "Location",
      width: Number(columnWidth),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: Number(columnWidth),
      renderCell: (params) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const navigate = useNavigate();
        return (
          <Button
            onClick={() =>
              navigate(`/tracker/status/orderProgress`, {
                state: { srNumber: params.row.orderNumber },
              })
            }
            startIcon={
              <FiEye
                style={{
                  color: "#143D60",
                  alignContent: "center",
                  alignItems: "center",
                }}
              />
            }
            style={{
              padding: "10px",
              color: "white",
              alignContent: "center",
              alignItems: "center",
              border: "none",
              borderRadius: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          ></Button>
        );
      },
    },
  ];

  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<any[]>([]);

  const paginationModel = { page: 0, pageSize: 10 };

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("enterprise_portal_access_Token");
      const data = {
        customerReferenceNumber: getCustomerReferenceNumber(),
      };
      const response = await axios.get(
        `${BASE_URL}/api/v1/EnterprisePortal.ServiceFulfilmentAPI/api/ServiceFulfilment/GetOrderList?customerReferenceNumber=${data.customerReferenceNumber}`,

        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.isSuccess) {
        const formattedRows = response.data.result.map((item: any) => ({
          id: item!.orderNumber,
          orderNumber: item!.orderNumber,
          sltOrderSubType: item!.sltOrderSubType,
          sltOrderSubtypeCondition: item!.sltOrderSubtypeCondition,
          status: item!.status,
          accountAddress: item!.accountAddress,
          accountAddressCity: item!.accountAddressCity,
          productName: item!.productName,
          serviceId: item!.serviceId,
          sltCustomerAccount: item!.sltCustomerAccount,
          sltCustomerAccountNumber: item!.sltCustomerAccountNumber,
          sltbssBillingAccountNumber: item!.sltbssBillingAccountNumber,
          orderCreatedDate: item!.orderCreatedDate,
          statusUpdatedDate: item!.statusUpdatedDate,
        }));
        setRows(formattedRows);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return (
    <div className="page-container">
      <MainLoader show={loading} />

      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          {
            path: "/tracker/status/orderProgress",
            title: "Order Complaint Status",
          },
          {
            path: "/tracker/status/orderProgress/ongoing",
            title: "All Ongoing Orders",
          },
        ]}
      />
      <Title>All Ongoing Orders</Title>
      {rows.length > 0 ? (
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "100%",
            minHeight: 300,
            marginTop: "10px",
            overflow: "auto",
            p: 1,
            mx: "auto",
            width: "100%",
            "& .MuiTablePagination-root": {
              display: "flex",
              alignItems: "baseline",
              justifyContent: "flex-end",
              minHeight: "56px",
              gap: "8px",
            },
            "& .MuiTablePagination-spacer": {
              order: 2,
            },
            "& .MuiTablePagination-selectLabel": {
              order: 0,
              display: "flex",
              alignItems: "baseline",
              padding: "15px",
              height: "36px",
            },
            "& .MuiTablePagination-select": {
              order: 1,
              display: "flex",
              alignItems: "center",
              height: "36px",
            },
            "& .MuiTablePagination-displayedRows": {
              order: 3,
              display: "flex",
              padding: "15px",
              alignItems: "baseline",
              height: "36px",
            },
            "& .MuiTablePagination-actions": {
              order: 4,
              display: "flex",
              alignItems: "center",
              marginLeft: "auto",
            },
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{ pagination: { paginationModel } }}
            pageSizeOptions={[5, 10, 15]}
            sx={{ border: 0 }}
          />
        </Paper>
      ) : (
        <Box
          sx={{
            mt: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <EmptyPlaceholder />
          <br />
          No Ongoing Orders....
        </Box>
      )}
    </div>
  );
};

export default OngoingOrderComplainList;
